import apiClient, { getAccessToken } from './api';
import { boundedWait } from '../utils/boundedWait';

/**
 * First-party product event tracking (phase 1: Shop).
 * Never throws, never blocks UI, no-op when logged out.
 * Design: .claude/es-upgrade/TRACKING-SHOP-EVENTS-DESIGN.md
 */

export type TrackProps = Record<string, string | number | boolean | undefined | Array<Record<string, string | number>>>;

interface QueuedEvent {
  name: string;
  props: TrackProps;
  ts: number;
  /** Identity epoch at track() time; events from an older identity are never sent. */
  epoch: number;
}

const MAX_UNLOAD_BATCHES = 2;
const RESET_MAX_WAIT_MS = 1500;

const FLUSH_INTERVAL_MS = 5000;
const FLUSH_SIZE = 20;
const MAX_QUEUE = 200;
const SESSION_KEY = 'aa_track_sid';
const ENDPOINT = '/api/events';

let queue: QueuedEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let listenersInstalled = false;
let identityEpoch = 0;

function randomId(): string {
  try {
    const bytes = new Uint8Array(12);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return `${Date.now().toString(16)}${Math.random().toString(16).slice(2, 10)}`;
  }
}

export function getTrackingSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = randomId();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return randomId();
  }
}

function buildUrl(): string {
  const base = (apiClient.defaults.baseURL ?? '').replace(/\/$/, '');
  return `${base}${ENDPOINT}`;
}

function takeBatch(): QueuedEvent[] {
  // Stale-identity events (queued before a login/logout/God Mode switch) are discarded, never re-attributed.
  const batch = queue.splice(0, FLUSH_SIZE).filter((e) => e.epoch === identityEpoch);
  return batch;
}

/** Strip client-only fields before sending. */
function wire(batch: QueuedEvent[]): Array<{ name: string; props: TrackProps; ts: number }> {
  return batch.map(({ name, props, ts }) => ({ name, props, ts }));
}

function send(batch: QueuedEvent[], unloading: boolean): void {
  const token = getAccessToken();
  if (!token || batch.length === 0) return;
  const events = wire(batch);
  const body = JSON.stringify({ sessionId: getTrackingSessionId(), events });
  const url = buildUrl();

  if (unloading) {
    // sendBeacon cannot carry the Authorization header (it would always 401), so unload uses fetch keepalive.
    // Where fetch is unavailable the last batch is lost on tab close.
    try {
      if (typeof fetch === 'function') {
        void fetch(url, {
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body,
        }).catch(() => undefined);
      }
    } catch {
      /* swallow */
    }
    return;
  }

  apiClient.post(ENDPOINT, { sessionId: getTrackingSessionId(), events }).catch(() => undefined);
}

export function flushTracking(unloading = false): void {
  try {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    if (!getAccessToken()) {
      queue = [];
      return;
    }
    let batches = 0;
    while (queue.length > 0) {
      if (unloading && batches >= MAX_UNLOAD_BATCHES) {
        queue = [];
        break;
      }
      send(takeBatch(), unloading);
      batches++;
    }
  } catch {
    /* tracking must never break the UI */
  }
}

function installListeners(): void {
  if (listenersInstalled || typeof window === 'undefined') return;
  listenersInstalled = true;
  try {
    window.addEventListener('pagehide', () => flushTracking(true));
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flushTracking(true);
    });
  } catch {
    /* swallow */
  }
}

export function track(name: string, props: TrackProps = {}): void {
  try {
    if (!getAccessToken()) return; // not logged in: no-op
    installListeners();
    if (queue.length >= MAX_QUEUE) queue.shift();
    queue.push({ name, props, ts: Date.now(), epoch: identityEpoch });
    if (queue.length >= FLUSH_SIZE) {
      flushTracking();
    } else if (!timer) {
      timer = setTimeout(() => flushTracking(), FLUSH_INTERVAL_MS);
    }
  } catch {
    /* swallow */
  }
}

/**
 * Call on login, logout, token swap, and God Mode enter/exit, and AWAIT it before swapping the
 * token/context. Sends what is queued under the CURRENT identity with that identity's captured
 * Authorization header, then starts a new identity epoch and rotates the tracking session id so one
 * browser session never mixes two identities. Never rejects.
 */
export async function resetTracking(): Promise<void> {
  // Capture the OLD identity before anything changes: token, session id, queued events of this epoch.
  const token = getAccessToken();
  const sessionId = getTrackingSessionId();
  const epoch = identityEpoch;
  const pending = queue.filter((e) => e.epoch === epoch);
  queue = [];
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  identityEpoch++;
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* swallow */
  }
  if (!token || pending.length === 0) return;
  // fetch with the captured Authorization header (apiClient's interceptor would read the NEW token).
  const sends: Array<Promise<unknown>> = [];
  for (let i = 0; i < pending.length; i += FLUSH_SIZE) {
    try {
      sends.push(
        fetch(buildUrl(), {
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ sessionId, events: wire(pending.slice(i, i + FLUSH_SIZE)) }),
        }).catch(() => undefined)
      );
    } catch {
      /* swallow */
    }
  }
  // Bounded: logout / login / God Mode must never wait more than RESET_MAX_WAIT_MS on tracking. The sends
  // keep running in the background (keepalive) after the wait gives up.
  await boundedWait(sends, RESET_MAX_WAIT_MS);
}
